// @wc-ignore-file

/**
 * CR-288 D2: the page a signed-out visitor asked for (e.g. /connect/notion),
 * carried through sign-in so they come back to it.
 *
 * The value is URL input (`/login?redirect=`), so it is only ever used once
 * `safeRedirectPath` has accepted it: a same-origin relative path, never a
 * scheme, a protocol-relative `//host`, a backslash (browsers read `/\host`
 * as `//host`) or a control character (browsers strip tabs/newlines, turning
 * `/\t/host` into `//host`). That keeps the return-to from being an open
 * redirect. The auth pages themselves are refused too, so a return-to can
 * never loop back onto /login. Every sink anchors the path on the app origin
 * (Supabase gets `${origin}${path}`, the router resolves it on its origin).
 *
 * The path also goes into localStorage for 30 minutes: when the email link
 * opens in another tab, or the provider drops the path (Supabase falls back to
 * the Site URL for a URL outside its redirect allowlist), the post-auth landing
 * reads it once and clears it.
 */

export const POST_AUTH_REDIRECT_KEY = "kairo.postAuthRedirect";
export const POST_AUTH_REDIRECT_TTL_MS = 30 * 60 * 1000;

const MAX_REDIRECT_LENGTH = 2048;
// The auth pages (a return-to there would loop) and the OAuth callback (it
// expects its own code/state, not Supabase's).
const DENIED_PAGES = ["/login", "/signup", "/account/integrations/callback"];
// A fixed base only used to resolve the path: any change of origin means the
// value was not a plain path.
const PARSE_BASE = "https://kairo.invalid";

/** Space, control characters and DEL: browsers strip or mangle them in URLs. */
function hasControlChar(value: string): boolean {
  for (let i = 0; i < value.length; i += 1) {
    const code = value.charCodeAt(i);
    if (code <= 0x20 || code === 0x7f) {
      return true;
    }
  }
  return false;
}

/** The value as a same-origin relative path, or undefined when it is anything else. */
export function safeRedirectPath(value: unknown): string | undefined {
  if (typeof value !== "string") {
    return undefined;
  }
  if (value.length === 0 || value.length > MAX_REDIRECT_LENGTH) {
    return undefined;
  }
  if (!value.startsWith("/") || value.startsWith("//") || value.includes("\\")) {
    return undefined;
  }
  if (hasControlChar(value)) {
    return undefined;
  }

  // The path part must also be plain once decoded: no encoded backslash,
  // double slash or control character (`/%5Cx.example`, `/%2F%2Fx.example`), so
  // a future sink that decodes the value can never turn it into `//host`.
  const rawPath = value.split(/[?#]/, 1)[0] ?? "";
  let decodedPath: string;
  try {
    decodedPath = decodeURIComponent(rawPath);
  } catch {
    return undefined;
  }
  if (decodedPath.includes("\\") || decodedPath.includes("//") || hasControlChar(decodedPath)) {
    return undefined;
  }

  let url: URL;
  try {
    url = new URL(value, PARSE_BASE);
  } catch {
    return undefined;
  }
  // Reject anything the parser had to normalize (dot segments such as
  // `/.//evil.com`): the value is kept verbatim, so it must already be canonical.
  if (url.origin !== PARSE_BASE || url.pathname !== rawPath) {
    return undefined;
  }
  const pathname = url.pathname.toLowerCase();
  if (DENIED_PAGES.some((page) => pathname === page || pathname.startsWith(`${page}/`))) {
    return undefined;
  }

  return value;
}

type StoredRedirect = { path: string; expiresAt: number };

function readStored(): StoredRedirect | undefined {
  try {
    const raw = window.localStorage.getItem(POST_AUTH_REDIRECT_KEY);
    if (!raw) {
      return undefined;
    }
    const parsed = JSON.parse(raw) as Partial<StoredRedirect> | null;
    const path = safeRedirectPath(parsed?.path);
    if (!path || typeof parsed?.expiresAt !== "number" || parsed.expiresAt < Date.now()) {
      return undefined;
    }
    return { path, expiresAt: parsed.expiresAt };
  } catch {
    return undefined;
  }
}

/** Remember the return-to for the post-auth landing (unsafe values are not stored). */
export function storePostAuthRedirect(path: string): void {
  const safe = safeRedirectPath(path);
  if (!safe) {
    return;
  }
  try {
    const entry: StoredRedirect = { path: safe, expiresAt: Date.now() + POST_AUTH_REDIRECT_TTL_MS };
    window.localStorage.setItem(POST_AUTH_REDIRECT_KEY, JSON.stringify(entry));
  } catch {
    // Storage blocked (private mode…): the URL-carried path still works.
  }
}

export function clearPostAuthRedirect(): void {
  try {
    window.localStorage.removeItem(POST_AUTH_REDIRECT_KEY);
  } catch {
    // Nothing to clear when storage is blocked.
  }
}

/** The stored return-to, read once: it is cleared whatever it holds. */
export function consumePostAuthRedirect(): string | undefined {
  const stored = readStored();
  clearPostAuthRedirect();
  return stored?.path;
}

/**
 * Clear the stored return-to once the user is on its page (the URL-carried
 * path worked), so a later sign-in does not send them there again.
 */
export function clearPostAuthRedirectIfReached(pathname: string): void {
  const stored = readStored();
  if (!stored) {
    return;
  }
  if (new URL(stored.path, PARSE_BASE).pathname === pathname) {
    clearPostAuthRedirect();
  }
}
